import { Building2, CalendarOff, CheckCircle2, Clock, Users } from 'lucide-react';
import { Carte, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import type {
  EtatDemiJournee,
  SessionEmargeable,
  TypeJour,
} from '../../lib/useEmargementSession';
import type { DemiJournee } from '../../lib/useEmargement';

/**
 * Première étape du parcours : quelle formation, quel groupe, quel cycle.
 *
 * Plusieurs formations peuvent se tenir le même jour dans la même antenne. La
 * sélection porte donc sur la **session**, jamais sur la formation seule : deux
 * groupes d'une même formation partageraient sinon une feuille d'émargement.
 *
 * Chaque session affiche l'état de ses deux cycles avant qu'on les ouvre. Un
 * cycle déjà commencé se reprend d'un geste — c'est le cas de la tablette qu'on
 * a refermée au milieu de l'appel, et il ne doit pas ressembler à un recommencement.
 */

const ICONES_JOUR: Partial<Record<TypeJour, typeof Building2>> = {
  ENTREPRISE: Building2,
  VACANCES: CalendarOff,
  FERIE: CalendarOff,
};

function EtatCycle({
  etat,
  onOuvrir,
  desactive,
}: {
  etat: EtatDemiJournee;
  onOuvrir: (demiJournee: DemiJournee) => void;
  desactive: boolean;
}): JSX.Element {
  const commence = etat.seanceId !== null;
  const termine = commence && etat.restants === 0;

  return (
    <button
      type="button"
      onClick={() => onOuvrir(etat.demiJournee)}
      disabled={desactive}
      className={`flex min-h-[96px] flex-1 flex-col justify-between gap-2 rounded-2xl border-2 p-4 text-left transition [@media(pointer:coarse)]:min-h-[112px] ${
        desactive
          ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
          : termine
            ? 'border-codialis-mint bg-codialis-sand hover:brightness-[0.98]'
            : commence
              ? 'border-codialis-sky bg-white hover:bg-gray-50'
              : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-base font-bold text-codialis-navy">
          {etat.libelle}
        </span>
        {termine ? (
          <Pastille texte="Terminé" ton="success" />
        ) : commence ? (
          <Pastille texte={etat.appelValide ? 'Signatures' : 'Appel en cours'} ton="info" />
        ) : (
          <Pastille texte="À ouvrir" ton="neutral" />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-codialis-gray">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {etat.attendus} attendu{etat.attendus > 1 ? 's' : ''}
        </span>
        {commence && (
          <>
            <span className="inline-flex items-center gap-1 text-codialis-navy">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              {etat.signes} signé{etat.signes > 1 ? 's' : ''}
            </span>
            <span>{etat.absents} absent{etat.absents > 1 ? 's' : ''}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {etat.restants} en attente
            </span>
          </>
        )}
      </div>
    </button>
  );
}

export function SelectionSession({
  sessions,
  chargement,
  erreur,
  onOuvrir,
}: {
  sessions: SessionEmargeable[];
  chargement: boolean;
  erreur: string | null;
  onOuvrir: (sessionId: string, demiJournee: DemiJournee) => void;
}): JSX.Element {
  if (chargement) return <Chargement libelle="Recherche des sessions du jour…" />;
  if (erreur) return <MessageErreur message={erreur} />;

  if (sessions.length === 0) {
    return (
      <Carte titre="Aucune session ce jour-là">
        <p className="p-4 text-sm text-codialis-gray sm:p-6">
          Aucun groupe n’est en période de formation à cette date sur le périmètre
          sélectionné. Vérifiez la date, l’antenne, et les dates de début et de fin des
          sessions dans « Formations ».
        </p>
      </Carte>
    );
  }

  return (
    <div className="space-y-4">
      {sessions.map((session) => {
        const Icone = session.journee.typeJour
          ? ICONES_JOUR[session.journee.typeJour]
          : undefined;

        return (
          <Carte
            key={session.sessionId}
            titre={session.session}
            actions={
              <>
                <Pastille texte={session.siteCode} ton="neutral" />
                <Pastille
                  texte={session.journee.libelle}
                  ton={session.journee.emargeable ? 'info' : 'warning'}
                />
              </>
            }
            sansBordure
          >
            <div className="space-y-3 p-4 sm:p-6">
              <p className="text-xs text-codialis-gray">
                {session.formationTitre} ({session.formationCode}) · {session.siteNom} ·{' '}
                {session.effectif} apprenant
                {session.effectif > 1 ? 's' : ''} en parcours
              </p>

              {session.journee.emargeable ? (
                <div className="flex flex-col gap-3 sm:flex-row">
                  {session.demiJournees.map((etat) => (
                    <EtatCycle
                      key={etat.demiJournee}
                      etat={etat}
                      desactive={false}
                      onOuvrir={(demiJournee) => onOuvrir(session.sessionId, demiJournee)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
                  {Icone && <Icone className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />}
                  <p>{session.journee.raison}</p>
                </div>
              )}
            </div>
          </Carte>
        );
      })}
    </div>
  );
}
